import React from 'react';
import Header from '../../components/ui/Header';
import SEO from '../../components/SEO';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { generatePageSEO } from '../../utils/seoData';

const BuildingInformation = () => {
  const seoData = generatePageSEO('building');

  const buildingFeatures = [
    {
      id: 1,
      icon: "Building",
      title: "Estrutura Comercial",
      description: "Edifício comercial funcional com espaços adaptados para diferentes tipos de negócios",
      details: [
        "Pavimento térreo comercial",
        "Área comum compartilhada",
        "Iluminação adequada",
        "Estrutura sólida e bem construída"
      ]
    },
    {
      id: 2,
      icon: "Shield",
      title: "Segurança Básica",
      description: "Medidas de segurança adequadas para garantir tranquilidade dos usuários",
      details: [
        "Iluminação externa",
        "Área monitorada",
        "Acesso controlado",
        "Local seguro e protegido"
      ]
    },
    {
      id: 3,
      icon: "Zap",
      title: "Infraestrutura Básica",
      description: "Infraestrutura essencial para funcionamento dos negócios",
      details: [
        "Energia elétrica estável",
        "Estação de tratamento de esgoto própria",
        "Distribuição para todo o prédio",
        "Área comum funcional"
      ]
    },
    {
      id: 4,
      icon: "Leaf",
      title: "Sustentabilidade Básica",
      description: "Práticas básicas de sustentabilidade e cuidado com o meio ambiente",
      details: [
        "Gestão de resíduos",
        "Uso consciente de recursos",
        "Área verde preservada",
        "Práticas sustentáveis"
      ]
    }
  ];

  const buildingStats = [
    { label: "Área Total", value: "2.500 m²", icon: "Maximize" },
    { label: "Lojas", value: "25+", icon: "Store" },
    { label: "Vagas", value: "50", icon: "Car" },
    { label: "Andares", value: "1", icon: "Building" },
    { label: "Ano de Construção", value: "2020", icon: "Calendar" },
    { label: "Tipo", value: "Comercial", icon: "Building" }
  ];

  const amenities = [
    {
      category: "Comercial",
      items: [
        { name: "Lojas Diversas", icon: "Store" },
        { name: "Área Comum", icon: "Users" },
        { name: "Estacionamento", icon: "Car" },
        { name: "Acesso Principal", icon: "DoorOpen" }
      ]
    },
    {
      category: "Serviços",
      items: [
        { name: "Tratamento de esgoto próprio", icon: "Droplets" },
        { name: "Energia Elétrica", icon: "Zap" },
        { name: "Limpeza", icon: "Shirt" },
        { name: "Manutenção", icon: "Wrench" }
      ]
    },
    {
      category: "Básico",
      items: [
        { name: "Iluminação", icon: "Lightbulb" },
        { name: "Ventilação", icon: "Wind" },
        { name: "Área Verde", icon: "TreePine" },
        { name: "Segurança", icon: "Shield" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO {...seoData} />
      <Header />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary via-primary/90 to-accent py-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Icon name="Building" size={16} className="text-white" />
              <span className="text-white text-sm font-medium">
                Sobre o Edifício
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
              Estação Marina da Rocha
              <br />
              <span className="text-accent">Estrutura e Funcionalidade</span>
            </h1>
            
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Um edifício comercial funcional e bem estruturado, oferecendo 
              espaços adequados para diferentes tipos de negócios e serviços, 
              com infraestrutura básica e segurança adequada
            </p>
          </div>
        </section>

        {/* Building Stats */}
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {buildingStats.map((stat, index) => (
                <div key={index} className="text-center p-6 bg-card rounded-xl border border-border">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name={stat.icon} size={24} className="text-primary" />
                  </div>
                  <div className="text-2xl font-bold text-foreground mb-1">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Building Features */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
                Características do Edifício
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Estrutura sólida e funcional que oferece o essencial para o 
                funcionamento adequado de negócios comerciais
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {buildingFeatures.map((feature) => (
                <div key={feature.id} className="bg-card rounded-xl border border-border p-8 hover:shadow-warm transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon name={feature.icon} size={32} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-foreground mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground mb-4 leading-relaxed">
                        {feature.description}
                      </p>
                      <ul className="space-y-2">
                        {feature.details.map((detail, index) => (
                          <li key={index} className="flex items-center text-sm text-muted-foreground">
                            <Icon name="Check" size={16} className="text-green-600 mr-2" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Amenities */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
                Comodidades e Serviços
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Infraestrutura básica e serviços essenciais para o funcionamento 
                adequado dos estabelecimentos comerciais
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {amenities.map((category, index) => (
                <div key={index} className="bg-card rounded-xl border border-border p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-6 text-center">
                    {category.category}
                  </h3>
                  <div className="space-y-4">
                    {category.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <Icon name={item.icon} size={16} className="text-primary" />
                        </div>
                        <span className="text-foreground font-medium">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Location & Access */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-6">
                  Localização Estratégica
                </h2>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                  Localizado no Altiplano Leste - Paranoá, o Estação Marina da Rocha 
                  oferece acesso conveniente e localização estratégica para negócios locais.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Icon name="MapPin" size={20} className="text-primary" />
                    <span className="text-foreground">Altiplano Leste - Paranoá, Brasília - DF</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Car" size={20} className="text-primary" />
                    <span className="text-foreground">50 vagas de estacionamento</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Navigation" size={20} className="text-primary" />
                    <span className="text-foreground">Fácil acesso por transporte público</span>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="h-64 lg:h-80">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3839.123456789!2d-47.8901234!3d-15.7801234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x935a23a3873fee5b%3A0x32b4613b357c717c!2sAltiplano%20Leste%20-%20Parano%C3%A1%2C%20Bras%C3%ADlia%20-%20DF!5e0!3m2!1spt-BR!2sbr!4v1760316412799!5m2!1spt-BR!2sbr"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Localização da Estação Marina da Rocha - Altiplano Leste - Paranoá, Brasília"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-24 bg-gradient-to-r from-primary to-primary/80">
          <div className="max-w-4xl mx-auto text-center px-4">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-6">
              Interessado em Fazer Parte da Nossa Comunidade?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Conheça as oportunidades disponíveis e como sua empresa pode se estabelecer 
              no Estação Marina da Rocha
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="secondary"
                size="lg"
                className="bg-white text-primary hover:bg-white/90 font-semibold px-8"
              >
                Ver Lojas Disponíveis
                <Icon name="Store" size={20} className="ml-2" />
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-white hover:text-primary font-semibold px-8"
              >
                Entre em Contato
                <Icon name="Mail" size={20} className="ml-2" />
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default BuildingInformation;
